import { noteToFreq, pinkNoise, pluck, whiteNoise } from './synth';

/**
 * 音。すべて Web Audio で合成する。音源ファイルは持たない。
 *
 * 方針は 3 つ。
 *  - **潜ったら世界がこもる。** 中心動作であるダイブに、視覚だけでなく聴覚の変化を与える。
 *    マスターに低域通過フィルタを置き、潜行中だけ閉じる。
 *  - **波の音がゲーム状態に反応する。** 崩れが近いほど泡の音が前に出る。
 *    危険が耳でも分かるので、画面外から迫るものへの備えができる。
 *  - **鳴らないより黙る。** 自動再生は禁止されているので、最初のタップまで音は作らない。
 */

type Ctx = AudioContext;

const MUTE_KEY = 'etw:muted';

/** ウクレレ風のコード進行（A4 からの半音数）。ワイキキの明るい響き。 */
const PROGRESSION: readonly (readonly number[])[] = [
  [3, 7, 10, 15], // C
  [0, 3, 7, 12], // Am
  [-4, 0, 3, 8], // F
  [-2, 2, 5, 10], // G
];
/** 1 小節 8 分音符 8 つ分の、爪弾く順番。 */
const ARPEGGIO = [0, 2, 1, 3, 2, 1, 3, 2] as const;
const BPM = 88;

export class AudioEngine {
  private ctx: Ctx | null = null;
  private master!: GainNode;
  /** 潜行中に閉じる。世界全体をこもらせる。 */
  private underwater!: BiquadFilterNode;
  private ambientGain!: GainNode;
  private foamGain!: GainNode;
  private musicGain!: GainNode;

  private pinkBuffer: AudioBuffer | null = null;
  private whiteBuffer: AudioBuffer | null = null;
  private readonly pluckCache = new Map<number, AudioBuffer>();

  private musicTimer: ReturnType<typeof setInterval> | null = null;
  private nextNoteTime = 0;
  private step = 0;

  private muted = false;
  private failed = false;

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === '1';
    } catch {
      this.muted = false;
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get isReady(): boolean {
    return this.ctx !== null && !this.failed;
  }

  /**
   * 最初のユーザー操作で呼ぶ。ここで初めて AudioContext を作る。
   * ブラウザの自動再生制限があるため、これより前に音を作っても鳴らない。
   */
  unlock(): void {
    if (this.failed) return;
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) {
        this.failed = true;
        return;
      }
      const ctx = new Ctor();
      this.ctx = ctx;
      void ctx.resume();

      this.master = ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 1;

      // 世界全体を通す低域通過。潜ると閉じる。
      this.underwater = ctx.createBiquadFilter();
      this.underwater.type = 'lowpass';
      this.underwater.frequency.value = 20000;
      this.underwater.Q.value = 0.7;
      this.underwater.connect(this.master);
      this.master.connect(ctx.destination);

      this.pinkBuffer = pinkNoise(ctx, 4);
      this.whiteBuffer = whiteNoise(ctx, 2);

      this.buildAmbient(ctx);
      this.musicGain = ctx.createGain();
      this.musicGain.gain.value = 0;
      this.musicGain.connect(this.underwater);
    } catch {
      this.failed = true;
      this.ctx = null;
    }
  }

  /** 寄せては返す波の地鳴りと、砕けた泡の白い音。 */
  private buildAmbient(ctx: Ctx): void {
    // ── うねりの地鳴り ──
    const swell = ctx.createBufferSource();
    swell.buffer = this.pinkBuffer;
    swell.loop = true;

    const swellFilter = ctx.createBiquadFilter();
    swellFilter.type = 'lowpass';
    swellFilter.frequency.value = 700;
    swellFilter.Q.value = 0.9;

    this.ambientGain = ctx.createGain();
    this.ambientGain.gain.value = 0.34;

    swell.connect(swellFilter).connect(this.ambientGain).connect(this.underwater);
    swell.start();

    // 遅い揺らぎ。寄せては返す呼吸をフィルタと音量の両方に掛ける。
    const breathe = ctx.createOscillator();
    breathe.frequency.value = 0.07;
    const breatheDepth = ctx.createGain();
    breatheDepth.gain.value = 260;
    breathe.connect(breatheDepth).connect(swellFilter.frequency);
    breathe.start();

    const swellVol = ctx.createOscillator();
    swellVol.frequency.value = 0.05;
    const swellVolDepth = ctx.createGain();
    swellVolDepth.gain.value = 0.1;
    swellVol.connect(swellVolDepth).connect(this.ambientGain.gain);
    swellVol.start();

    // ── 泡の白い音。崩れが近いほど前に出る ──
    const foam = ctx.createBufferSource();
    foam.buffer = this.whiteBuffer;
    foam.loop = true;

    const foamFilter = ctx.createBiquadFilter();
    foamFilter.type = 'bandpass';
    foamFilter.frequency.value = 2400;
    foamFilter.Q.value = 0.6;

    this.foamGain = ctx.createGain();
    this.foamGain.gain.value = 0;

    foam.connect(foamFilter).connect(this.foamGain).connect(this.underwater);
    foam.start();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
    } catch {
      // 保存できなくても音の切り替え自体は効かせる。
    }
    if (!this.ctx) return;
    this.master.gain.setTargetAtTime(muted ? 0 : 1, this.ctx.currentTime, 0.05);
  }

  /**
   * 毎フレームの追従。
   * @param foamProximity 0（遠い）〜 1（崩れの先頭が目の前）
   */
  update(params: { diving: boolean; foamProximity: number; underwaterMix?: number }): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;

    // 潜ると 380 Hz まで閉じる。水中で高い音が届かないのと同じ。
    this.underwater.frequency.setTargetAtTime(params.diving ? 380 : 18000, now, 0.06);
    // 水中では地鳴りだけが残る。
    this.ambientGain.gain.setTargetAtTime(params.diving ? 0.42 : 0.3, now, 0.15);
    this.foamGain.gain.setTargetAtTime(
      Math.max(0, Math.min(1, params.foamProximity)) * (params.diving ? 0.05 : 0.16),
      now,
      0.12,
    );
  }

  // ── 効果音 ──────────────────────────────────

  /** ノイズを短く鳴らす。しぶき系はすべてこれの組み合わせ。 */
  private noiseBurst(options: {
    duration: number;
    gain: number;
    from: number;
    to: number;
    type?: BiquadFilterType;
    q?: number;
  }): void {
    const ctx = this.ctx;
    if (!ctx || !this.whiteBuffer) return;
    const now = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.whiteBuffer;
    source.playbackRate.value = 1;

    const filter = ctx.createBiquadFilter();
    filter.type = options.type ?? 'bandpass';
    filter.Q.value = options.q ?? 1;
    filter.frequency.setValueAtTime(options.from, now);
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, options.to), now + options.duration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(options.gain, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + options.duration);

    source.connect(filter).connect(gain).connect(this.underwater);
    source.start(now);
    source.stop(now + options.duration + 0.05);
  }

  /** 単音。取得音や合図に使う。 */
  private tone(freq: number, duration: number, gain: number, type: OscillatorType = 'sine'): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);

    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(gain, now + 0.01);
    env.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    osc.connect(env).connect(this.underwater);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  /** 潜る。上から下へ落ちる水音。 */
  dive(): void {
    this.noiseBurst({ duration: 0.42, gain: 0.3, from: 2600, to: 260, q: 0.8 });
    this.tone(180, 0.3, 0.1, 'sine');
  }

  /** 浮上。短く明るいしぶき。 */
  surface(): void {
    this.noiseBurst({ duration: 0.26, gain: 0.22, from: 900, to: 3400, q: 0.8 });
  }

  /** ドルフィンスルー成功。抜けた手応えと、澄んだ加点音。 */
  perfect(combo: number): void {
    this.noiseBurst({ duration: 0.3, gain: 0.24, from: 400, to: 3800, q: 0.9 });
    const step = Math.min(7, combo - 1);
    this.tone(noteToFreq(3 + step * 2), 0.42, 0.14, 'triangle');
    this.tone(noteToFreq(15 + step * 2), 0.34, 0.07, 'sine');
  }

  /** 波に乗った。低めの押し出す音。 */
  ride(): void {
    this.noiseBurst({ duration: 0.5, gain: 0.16, from: 300, to: 1500, q: 0.7 });
  }

  /** 目の前で崩れた。危険を知らせる短い合図。 */
  closeout(): void {
    this.tone(660, 0.12, 0.1, 'square');
    this.tone(880, 0.1, 0.06, 'square');
  }

  /** 巻かれた。低い唸りと泡。 */
  caught(): void {
    this.noiseBurst({ duration: 0.9, gain: 0.34, from: 1800, to: 160, q: 0.6 });
    this.tone(90, 0.7, 0.2, 'sawtooth');
    this.tone(62, 0.9, 0.14, 'sine');
  }

  /** 宝の取得。等級が上がるほど高く華やかに。 */
  pickup(points: number): void {
    const big = points >= 200;
    this.tone(noteToFreq(big ? 15 : 10), 0.2, big ? 0.14 : 0.08, 'triangle');
    if (big) {
      this.tone(noteToFreq(19), 0.3, 0.09, 'sine');
      this.tone(noteToFreq(22), 0.36, 0.06, 'sine');
    }
  }

  /** 帰還。短い和音で締める。 */
  finish(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    [3, 7, 10, 15].forEach((semi, i) => {
      setTimeout(() => this.tone(noteToFreq(semi), 0.7, 0.12, 'triangle'), i * 90);
    });
  }

  // ── BGM ────────────────────────────────────

  private pluckBuffer(freq: number): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const key = Math.round(freq);
    const cached = this.pluckCache.get(key);
    if (cached) return cached;
    // 撥弦は毎回作ると重いので、音ごとに 1 度だけ作って使い回す。
    const buffer = pluck(ctx, freq, 1.4, 0.994, 0.5);
    this.pluckCache.set(key, buffer);
    return buffer;
  }

  startMusic(): void {
    const ctx = this.ctx;
    if (!ctx || this.musicTimer) return;
    this.musicGain.gain.setTargetAtTime(0.5, ctx.currentTime, 1.2);
    this.nextNoteTime = ctx.currentTime + 0.1;
    this.step = 0;
    // 先読みして予約する。setInterval だけで鳴らすと揺れる。
    this.musicTimer = setInterval(() => this.scheduleMusic(), 30);
  }

  stopMusic(): void {
    const ctx = this.ctx;
    if (this.musicTimer) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (ctx) this.musicGain.gain.setTargetAtTime(0, ctx.currentTime, 0.4);
  }

  private scheduleMusic(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const eighth = 60 / BPM / 2;

    while (this.nextNoteTime < ctx.currentTime + 0.15) {
      const bar = Math.floor(this.step / ARPEGGIO.length) % PROGRESSION.length;
      const chord = PROGRESSION[bar];
      const semi = chord[ARPEGGIO[this.step % ARPEGGIO.length]];
      const buffer = this.pluckBuffer(noteToFreq(semi + 12));

      if (buffer) {
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        const gain = ctx.createGain();
        // 表拍を少し強く。機械的な粒立ちを避ける。
        gain.gain.value = this.step % 2 === 0 ? 0.16 : 0.1;
        source.connect(gain).connect(this.musicGain);
        source.start(this.nextNoteTime);
      }

      this.nextNoteTime += eighth;
      this.step += 1;
    }
  }

  /** 走行の開始と終了で BGM を切り替える。 */
  suspend(): void {
    this.stopMusic();
  }
}
